/* App.vue */

<template>
  <div style="display: contents">
    <div v-if="loading" class="loading-overlay">
      <div class="loading-content">
        <div class="loading-icon"></div>
        <p v-if="loadingMessage" class="loading-message">{{ loadingMessage }}</p>
      </div>
    </div>
    <dialog-manager></dialog-manager>
    <router-view @map-init="inited = true"></router-view>
    <router-view name="dialog" v-slot="{ Component }">
      <component :is="Component" v-if="Component"></component>
    </router-view>
    <router-view name="search"></router-view>
    <router-view name="toolbar"></router-view>
  </div>
</template>

<script>
import { ref, computed, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useMainStore } from '@/stores/main'
import { pick, defaults, zipObject, times, constant, throttle } from 'lodash'
import Dialog from './components/Dialog.vue'

export default {
  components: { 'DialogManager': Dialog },
  setup() {
    const route = useRoute()
    const mainStore = useMainStore()

    const inited = ref(false)
    const loadMapId = ref(null)

    // Computed
    const loading = computed(() => mainStore.loading)
    const loadingMessage = computed(() => mainStore.loadingMessage)

    // Throttled save function (saves at most every 1 second)
    const throttledSave = throttle(() => {
      mainStore.saveLocalStorageState()
    }, 1000)

    // Watch store properties and save to localStorage (throttled)
    // NEW: Watch shapesVersion instead of shapes.length to avoid deep reactivity
    watch(
      () => ({
        mode: mainStore.mode,
        toolType: mainStore.toolType,
        imperial: mainStore.imperial,
        activeStatusTab: mainStore.activeStatusTab,
        address: mainStore.address,
        displayAddress: mainStore.displayAddress,
        shapesVersion: mainStore.shapesVersion, // NEW: Version counter instead of shapes.length
        globalZIndex: mainStore.globalZIndex,
        mapEditingEnabled: mainStore.mapEditingEnabled,
        projectName: mainStore.projectName,
        uuid: mainStore.uuid,
        // Watch serialized versions to avoid deep traversal
        primaryUnits: JSON.stringify(mainStore.primaryUnits),
        mapCenter: JSON.stringify(mainStore.mapCenter),
        meta: JSON.stringify(mainStore.meta),
        mapZoom: JSON.stringify(mainStore.mapZoom),
        utm: JSON.stringify(mainStore.utm)
      }),
      () => {
        throttledSave()
      }
    )

    // Methods
    const setUtm = (utm) => {
      mainStore.setUtm(utm)
    }

    // Watchers
    // Watch route.params.mapId specifically for proper reactivity
    watch(() => route.params.mapId, (newMapId) => {
      if (newMapId) {
        loadMapId.value = newMapId
      }
    }, { immediate: true })

    // Separate watcher for UTM params
    watch(() => route.query, (query) => {
      if (Object.keys(query).length) {
        const utmKeys = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content']
        const picked = pick(query, utmKeys)
        const utm = defaults(picked, zipObject(utmKeys, times(utmKeys.length, constant(''))))
        setUtm(utm)
      }
    }, { immediate: true })

    watch(loadMapId, (newMapId) => {
      if (inited.value && newMapId) {
        mainStore.initializeStore({ mapId: newMapId })
      }
    })

    watch(inited, (initedVal) => {
      if (!initedVal) { return }
      if (loadMapId.value) {
        mainStore.initializeStore({ mapId: loadMapId.value })
        return
      }

      mainStore.initializeStore()
    }, { immediate: true })

    return {
      inited,
      loadMapId,
      loading,
      loadingMessage
    }
  }
}
</script>

<style scoped>
.loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}

.loading-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  background-color: var(--app-surface);
  border-radius: 8px;
  box-shadow: 0 4px 12px var(--app-toolbar-shadow);
}

.loading-icon {
  width: 3rem;
  height: 3rem;
  border: 4px solid var(--app-border);
  border-top-color: var(--app-primary);
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.loading-message {
  margin-top: 1rem;
  color: var(--app-text);
  font-size: 1.1rem;
  text-align: center;
}
</style>